import {EnumInterface, IDealer, IDealerFE, SelectElementModelBE, SelectElementModelFE,} from "./api/types/generic";
import dayjs from "dayjs";
import {AgGridReact} from "ag-grid-react";
import {RefObject} from "react";
import {RentalPeriodEntityResponse} from "./api/types/rentalPeriods";
import {RateEntityResponse, RateSelectFE} from "./api/types/rate";
import {AncillaryEntityResponse, AncillarySelectFE,} from "./api/types/ancillary";
import {LocationGroupProductCodeModelFE} from "./api/types/locationGroupProductCode";
import {AncillaryExceptionEntityResponse, AncillaryExceptionSelectFE,} from "./api/types/ancillaryException";
import {OWAncillaryEntityResponse} from "./api/types/owAncillary";
import {OWAncillaryExceptionEntityResponse, OWAncillaryExceptionSelectFE,} from "./api/types/owAncillaryException";
import {useTheme} from "@mui/material";
import {ColDef} from "ag-grid-community";
import {Location} from "./api/types/location";
import i18n from "i18next";
import { CommercialCategorySelectFE, Translation } from "./api/types/commercialCategory"; // Added Translation import

/* FROM BE TO FE - START */
export const fromSingleGenericBeToSingleGenericFe = <
    T extends SelectElementModelBE,
    D extends SelectElementModelFE
>(
    selectElementModelBe: T,
    activeTenantId?: string
): D => {
    // @ts-ignore
    const selectElementModelFe: D = {};
    Object.entries(selectElementModelBe).forEach(([key, value]) => {
        if (key === "id") {
            selectElementModelFe["code"] = value;
        } else if (key === "name") {
            // Check if selectElementModelBe has a 'labels' property and it's an array
            if ('labels' in selectElementModelBe && Array.isArray((selectElementModelBe as any).labels) && (selectElementModelBe as any).labels.length > 0 && activeTenantId) {
                const localizedLabel = (selectElementModelBe as any).labels.find(
                    (item: Translation) => item.country === activeTenantId
                );
                selectElementModelFe["label"] = localizedLabel ? localizedLabel.label : (value as string);
            } else {
                selectElementModelFe["label"] = value as string;
            }
        } else {
            // @ts-ignore
            selectElementModelFe[key] = value;
        }
    });

    return selectElementModelFe;
};

export const fromGenericBeToGenericFe = <
    T extends SelectElementModelBE,
    D extends SelectElementModelFE
>(
    selectElementModelBe: T[],
    activeTenantId?: string
): D[] => {
    const selectElementModelFe: D[] = [];

    selectElementModelBe?.forEach((element: T) => {
        selectElementModelFe?.push(fromSingleGenericBeToSingleGenericFe(element, activeTenantId));
    });

    return selectElementModelFe;
};

export const fromEnumToFe = (
    enumResponse: EnumInterface
): SelectElementModelFE[] => {
    return Object.entries(enumResponse).map(([key, value]) => {
        return {
            code: key,
            label: value,
        };
    });
};
/* FROM BE TO FE - END */

/* FROM FE TO BE - START */
export const fromSingleGenericFeToSingleGenericBe = <
    T extends SelectElementModelFE,
    D extends SelectElementModelBE
>(
    selectElementModelFe: T
): D => {
    // @ts-ignore
    const selectElementModelBe: D = {};
    Object.entries(selectElementModelFe).forEach(([key, value]) => {
        if (key === "code") {
            selectElementModelBe["id"] = value;
        } else if (key === "label") {
            selectElementModelBe["name"] = value as string;
        } else {
            // @ts-ignore
            selectElementModelBe[key] = value;
        }
    });

    return selectElementModelBe;
};

export const fromGenericFeToGenericBe = <
    T extends SelectElementModelFE,
    D extends SelectElementModelBE
>(
    selectElementModelFe: T[]
): D[] => {
    const selectElementModelBe: D[] = [];

    selectElementModelFe.forEach((element: T) => {
        selectElementModelBe.push(fromSingleGenericFeToSingleGenericBe(element));
    });

    return selectElementModelBe;
};
/* FROM FE TO BE - END */

export const fromLocationGroupProductCodeBeToFe = (
    selectElementModelBe: SelectElementModelBE[],
    productCodeId: string
): LocationGroupProductCodeModelFE[] => {
    return selectElementModelBe.map((element: SelectElementModelBE) => {
        return {
            code: element.id,
            label: element.name,
            productCodeId,
        };
    })
};

export const fromRateBeToFe = (
    selectElementModelBe: RateEntityResponse[]
): RateSelectFE[] => {
    return selectElementModelBe.map((element: RateEntityResponse) => {
        return {
            ...element,
            code: element.id,
            label: `${element.id} - ${element.name}`,
            referenceTariff: element.referenceTariff
                ? {
                    id: (element.referenceTariff as RateSelectFE).code,
                    name: (element.referenceTariff as RateSelectFE).label,
                }
                : null,
            extraUnitRate: element.extraUnitRate
                ? {
                    id: (element.extraUnitRate as RateSelectFE).code,
                    name: (element.extraUnitRate as RateSelectFE).label,
                }
                : null,
        };
    });
};

export const fromRateBeToFeForRatePlan = (selectElementModelBe: RateEntityResponse[]): RateSelectFE[] => {
    return selectElementModelBe.map((element: RateEntityResponse) => {
        return {
            ...element,
            code: element.id,
            label: `${element.id}-${element.name} ${dateFormatterShort(
                element.reservationFrom
            )} - ${dateFormatterShort(element.reservationTo)} | ${dateFormatterShort(element.pickupDateFrom)} - ${dateFormatterShort(element.pickupDateTo)}`,
            referenceTariff: element.referenceTariff ? {
                id: (element.referenceTariff as RateSelectFE).code,
                name: (element.referenceTariff as RateSelectFE).label
            } : null,
            extraUnitRate: element.extraUnitRate ? {
                id: (element.extraUnitRate as RateSelectFE).code,
                name: (element.extraUnitRate as RateSelectFE).label
            } : null
        };
    }).sort((a, b) => b.id - a.id);
};

export const fromAncillaryBeToFe = (
    selectElementModelBe: AncillaryEntityResponse[]
): AncillarySelectFE[] => {
    return selectElementModelBe.map((element: AncillaryEntityResponse) => {
        return {
            code: element.id,
            label: element.id + " - " + element.nameAncillary || element.code,

            ancillaryCode: element.code,

            note: element.note,
            reservationFrom: element.reservationFrom,
            reservationTo: element.reservationTo,
            pickupFrom: element.pickupFrom,
            pickupTo: element.pickupTo,
            tpUnit: element.tpUnit,
            currency: element.currency,
            amountMin: element.amountMin,
            amountMax: element.amountMax,
            dealerId: element.dealerId,
            chargingWay: element.chargingWay,
            maxUnits: element.maxUnits,
            chargeType: element.chargeType,
            nameAncillary: element.nameAncillary,
            description: element.description,
            type: element.type,
            //exceptionListPack: element.exceptionListPack,
            subtype: element.subtype,
            segments: fromGenericBeToGenericFe(element.segments),
            cardinality: element.cardinality,
            sellDesk: element.sellDesk,
            passageToCounterNeeded: element.passageToCounterNeeded,
            applicationTiming: element.applicationTiming === null ? null : element.applicationTiming === "PICK_UP" ? "PICK UP" : "DROP OFF",
            toleranceTime: element.toleranceTime,
            // RF-02 (US-SYS-004) - 5 flag ancillary (deserializzazione lenient)
            flagResellableAtDeskPostRa: element.flagResellableAtDeskPostRa ?? false,
            flagResellableInSubstitution: element.flagResellableInSubstitution ?? false,
            flagDiscountApplicable: element.flagDiscountApplicable ?? false,
            timeToChange: element.timeToChange ?? null,
            timeToRemove: element.timeToRemove ?? null,
            additionalField: element.additionalField,
            tax: element.tax,
            taxCode: element.taxCode,
            isOneWay: element.isOneWay,
            dealer: element.dealer,
            legalEntity: element.legalEntity,
        };
    });
};

export const fromAncillaryExceptionBeToFe = (
    selectElementModelBe: AncillaryExceptionEntityResponse[]
): AncillaryExceptionSelectFE[] => {
    return selectElementModelBe.map(
        (element: AncillaryExceptionEntityResponse) => {
            return {
                code: element.id,
                label: element.name,

                commercialSegment: element.commercialSegment,
                commercialCategory: element.commercialCategory,
                prcAncillary: element.prcAncillary,
                relatedDiscountIncreasePercentage:
                element.relatedDiscountIncreasePercentage,
                relatedDiscountIncreaseFixed: element.relatedDiscountIncreaseFixed,
                timeUnitsMax: element.timeUnitsMax,

                note: element.note,
                reservationFrom: element.reservationFrom,
                reservationTo: element.reservationTo,
                pickupFrom: element.pickupFrom,
                pickupTo: element.pickupTo,
                tpUnit: element.tpUnit,
                currency: element.currency,
                amountMin: element.amountMin,
                amountMax: element.amountMax,
                chargingWay: element.chargingWay,
                chargeType: element.chargeType,
                description: element.description,
                type: element.type,
                subtype: element.subtype,
                cardinality: element.cardinality,
                sellDesk: element.sellDesk,
                toleranceTime: element.toleranceTime,
                additionalField: element.additionalField,
                dealer: element.dealer,
                legalEntity: element.legalEntity,
            };
        }
    );
};

export const fromOWAncillaryBeToFe = (
    selectElementModelBe: OWAncillaryEntityResponse[]
): AncillarySelectFE[] => {
    return selectElementModelBe.map((element: OWAncillaryEntityResponse) => {
        return {
            ancillaryCode: element.prcAncillary.code,
            code: element.prcAncillary.id,
            label: element.prcAncillary.nameAncillary,
            note: element.prcAncillary.note,
            reservationFrom: element.prcAncillary.reservationFrom,
            reservationTo: element.prcAncillary.reservationTo,
            pickupFrom: element.prcAncillary.pickupFrom,
            pickupTo: element.prcAncillary.pickupTo,
            tpUnit: element.prcAncillary.tpUnit,
            currency: element.prcAncillary.currency,
            amountMin: element.prcAncillary.amountMin,
            amountMax: element.prcAncillary.amountMax,
            dealerId: element.prcAncillary.dealerId,
            chargingWay: element.prcAncillary.chargingWay,
            maxUnits: element.prcAncillary.maxUnits,
            chargeType: element.prcAncillary.chargeType,
            nameAncillary: element.prcAncillary.nameAncillary,
            description: element.prcAncillary.description,
            type: element.prcAncillary.type,
            subtype: element.prcAncillary.subtype,
            segments: fromGenericBeToGenericFe(element.prcAncillary.segments),
            cardinality: element.prcAncillary.cardinality,
            sellDesk: element.prcAncillary.sellDesk,
            passageToCounterNeeded: element.prcAncillary.passageToCounterNeeded,
            applicationTiming:
                element.prcAncillary.applicationTiming === null
                    ? null
                    : element.prcAncillary.applicationTiming === "PICK_UP"
                        ? "PICK UP"
                        : "DROP OFF",
            toleranceTime: element.prcAncillary.toleranceTime,
            // RF-02 (US-SYS-004) - 5 flag ancillary (deserializzazione lenient)
            flagResellableAtDeskPostRa: element.prcAncillary.flagResellableAtDeskPostRa ?? false,
            flagResellableInSubstitution: element.prcAncillary.flagResellableInSubstitution ?? false,
            flagDiscountApplicable: element.prcAncillary.flagDiscountApplicable ?? false,
            timeToChange: element.prcAncillary.timeToChange ?? null,
            timeToRemove: element.prcAncillary.timeToRemove ?? null,
            additionalField: element.prcAncillary.additionalField,
            tax: element.prcAncillary.tax,
            taxCode: element.prcAncillary.taxCode,
            isOneWay: element.prcAncillary.isOneWay,
            dealer: element.prcAncillary.dealer as string,
            legalEntity: element.prcAncillary.legalEntity as string,
        };
    });
};

export const fromOWAncillaryExceptionBeToFe = (
    selectElementModelBe: OWAncillaryExceptionEntityResponse[]
): OWAncillaryExceptionSelectFE[] => {
    return selectElementModelBe.map(
        (element: OWAncillaryExceptionEntityResponse) => {
            return {
                code: element.id,
                label: element.name,

                commercialSegment: element.commercialSegment,
                commercialCategory: element.commercialCategory,
                prcAncillary: element.prcAncillary,
                relatedDiscountIncreasePercentage:
                element.relatedDiscountIncreasePercentage,
                relatedDiscountIncreaseFixed: element.relatedDiscountIncreaseFixed,
                timeUnitsMax: element.timeUnitsMax,

                note: element.note,
                reservationFrom: element.reservationFrom,
                reservationTo: element.reservationTo,
                pickupFrom: element.pickupFrom,
                pickupTo: element.pickupTo,
                tpUnit: element.tpUnit,
                currency: element.currency,
                amountMin: element.amountMin,
                amountMax: element.amountMax,
                chargingWay: element.chargingWay,
                chargeType: element.chargeType,
                description: element.description,
                type: element.type,
                subtype: element.subtype,
                cardinality: element.cardinality,
                sellDesk: element.sellDesk,
                toleranceTime: element.toleranceTime,
                additionalField: element.additionalField,
                tax: element.tax,
                dealer: element.dealer,
                legalEntity: element.legalEntity,
            };
        }
    );
};

export const fromDealerBeToFe = (dealers: IDealer[]): IDealerFE[] => {
    return dealers.map((dealer: IDealer) => ({
        code: dealer.id,
        label: dealer.name,
        legalEntity: dealer.legalEntity,
    }));
};
/* FROM BE TO FE - END */

/* FORMATTER - START */
export const dateFormatter = (date: any) => {
    return dayjs(date).format("L LT");
};

export const dateFormatterShort = (date: any) => {
    return dayjs(date).format("DD/MM/YY HH:mm");
};
/* FORMATTER - END */

/* AG GRID - START */
export const onGridReady = ({
                                initialSortColId,
                                gridRef,
                                createDatasource,
                                index,
                            }: {
    initialSortColId: string;
    gridRef: RefObject<AgGridReact>;
    createDatasource: Function;
    index?: number;
}) => {
    gridRef?.current?.columnApi?.applyColumnState({
        state: [{colId: initialSortColId, sort: "asc"}],
        defaultState: {sort: null},
    });
    gridRef?.current
        ? gridRef?.current?.api?.setServerSideDatasource(createDatasource(index))
        : // @ts-ignore
        gridRef?.api?.setServerSideDatasource(createDatasource(index));
};
/* AG GRID - END */

export const getCommercialCategoryLabel = (
    commercialCategoryList: SelectElementModelFE[],
    commercialCategoryId: number
) => {
    return commercialCategoryList?.find(
        (el: any) => el.code === commercialCategoryId
    )?.label;
};

/* PRICE MATRIX ROWS AND COLUMNS - START */
export const sortRentalPeriods = ({
                                      rentalPeriods,
                                  }: {
    rentalPeriods: RentalPeriodEntityResponse[];
}): RentalPeriodEntityResponse[] => {
    return rentalPeriods.sort(
        (a: RentalPeriodEntityResponse, b: RentalPeriodEntityResponse) => {
            if (!a.to) return 1;
            //ordinare i periodi in base all'unità di misura
            return a.to - b.from;
        }
    );
};

export const changeRentalPeriods = async ({
                                              newRentalPeriods,
                                              allRentalPeriodColumns,
                                              handleChangeAgGridStructure,
                                              initialCarGroupRows,
                                              carGroupRows,
                                              rentalPeriodsAbsoluteList,
                                              absoluteValue,
                                              getRentalPeriodsSorted
                                          }: {
    newRentalPeriods: SelectElementModelFE[];
    allRentalPeriodColumns: RentalPeriodEntityResponse[];
    handleChangeAgGridStructure: Function;
    initialCarGroupRows: string[];
    carGroupRows: string[];
    rentalPeriodsAbsoluteList?: RentalPeriodEntityResponse[];
    absoluteValue?: boolean;
    getRentalPeriodsSorted?: any;
}) => {
    const rentalPeriodsSubGroup: SelectElementModelFE[] = newRentalPeriods.filter(
        (el) => el.code !== 0
    );

    const actualRentalPeriods: RentalPeriodEntityResponse[] =
        absoluteValue ? (rentalPeriodsAbsoluteList as RentalPeriodEntityResponse[]).filter((rp: RentalPeriodEntityResponse) => {
            return rentalPeriodsSubGroup.find(
                (rpSub: SelectElementModelFE) => rpSub.code === rp.id
            );
        }) : allRentalPeriodColumns.filter((rp: RentalPeriodEntityResponse) => {
            return rentalPeriodsSubGroup.find(
                (rpSub: SelectElementModelFE) => rpSub.code === rp.id
            );
        });
    const rentalPeriodSorted: RentalPeriodEntityResponse[] = await getRentalPeriodsSorted(actualRentalPeriods)

    handleChangeAgGridStructure({
        rentalPeriods: rentalPeriodSorted,
        carGroups:
            actualRentalPeriods.length === 0
                ? initialCarGroupRows
                : carGroupRows
    });
};

export const sortCarGroups = ({
                                  carGroups,
                              }: {
    carGroups: SelectElementModelFE[];
}): SelectElementModelFE[] => {
    return carGroups.sort((a: SelectElementModelFE, b: SelectElementModelFE) => {
        if (a.label < b.label) {
            return -1;
        }
        if (a.label > b.label) {
            return 1;
        }
        return 0;
    });
};

export const changeCarGroups = ({
                                    t,
                                    newCarGroups,
                                    handleChangeAgGridStructure,
                                    allRentalPeriodColumns,
                                    rentalPeriodColumns,
                                }: {
    t: Function;
    newCarGroups: string[];
    handleChangeAgGridStructure: Function;
    allRentalPeriodColumns: RentalPeriodEntityResponse[];
    rentalPeriodColumns: RentalPeriodEntityResponse[];
}) => {
    const carGroupsSubGroup = newCarGroups.filter(
        (el) => el !== t("pricing:actions.selectAll")
    );

    handleChangeAgGridStructure({
        rentalPeriods:
            carGroupsSubGroup.length === 0
                ? allRentalPeriodColumns
                : rentalPeriodColumns,
        carGroups: carGroupsSubGroup,
    });
};
/* PRICE MATRIX ROWS AND COLUMNS - END */

/* QUERY - START */
/**
 * LPP-24637 — Normalizza i filtri legalEntity/dealer in lista di codici.
 *
 * Lo stesso campo dei filtri puo' contenere due rappresentazioni diverse:
 * - i codici (string[]) quando vale il default del profilo utente o dopo il reset;
 * - gli oggetti SelectElementModelFE selezionati nella form dei filtri avanzati.
 *
 * Il servizio di pricing richiede almeno un valore (@NotEmpty su dealers/legalEntities):
 * se la selezione e' vuota o non risolvibile si torna al fallback del profilo utente,
 * evitando la chiamata con parametri vuoti che verrebbe rifiutata con 400 "Validation failure".
 */
export const resolveFilterCodes = (
    value: unknown,
    fallback: string[]
): string[] => {
    if ((value as SelectElementModelFE)?.code === "all") {
        return fallback;
    }
    if (!Array.isArray(value)) {
        return fallback;
    }
    const codes = (value as (string | SelectElementModelFE)[])
        .map((el) => (typeof el === "string" ? el : (el?.code as string)))
        .filter((el): el is string => Boolean(el));

    return codes.length > 0 ? codes : fallback;
};

export const buildQueryParams = (filters: any) => {
    return Object.entries(filters).reduce(
        (acc: string, [key, value]: [string, any]) => {
            if (value !== null && value !== undefined && value !== "") {
                return `${acc}${acc ? "&" : ""}${key}=${
                    typeof value === "string" ? value.trim() : value
                }`;
            }

            return `${acc}`;
        },
        ""
    );
};
/* QUERY - END */

export const useActionColDef = () => {
    const theme = useTheme();

    const actionColDef: ColDef = {
        field: "action",
        headerName: "",
        width: 60,
        maxWidth: 60,
        minWidth: 60,
        sortable: false,
        pinned: "right",
        cellStyle: {
            padding: 0,
            width: 60,
            maxWidth: 60,
            minWidth: 60,
            background: theme.palette.neutral.bright || theme.palette.neutral.light,
        },
        resizable: false,
    };

    return {
        actionColDef,
    };
};

export const getMobilityStores = async (
    currentLocations: Location[],
    user: any,
    postLocationAll: Function,
    putLocationAll: Function
): Promise<Location[]> => {
    const currentLocationsDictionary: { [key: string]: Location } = {};
    currentLocations.forEach((cl) => (currentLocationsDictionary[cl.id] = cl));

    const mobilityStoresFiltered = Array.from(new Set([...user.mobilityStores]));

    const insertLocations: Location[] = [];
    const updateLocations: Location[] = [];
    const sameLocations: Location[] = [];

    mobilityStoresFiltered.forEach((ms) => {
        // location nuova da persistere
        if (ms.sapId && !currentLocationsDictionary[ms.sapId]) {
            insertLocations.push({
                id: ms.sapId,
                name: ms.name,
                region: ms.region,
                active: true,
                percDiscountOverprice: [],
            });
        }
        // location già persistita ma da aggiornare
        else if (
            ms.sapId &&
            currentLocationsDictionary[ms.sapId] &&
            !(
                currentLocationsDictionary[ms.sapId].name === ms.name &&
                (ms.region
                    ? currentLocationsDictionary[ms.sapId].region === ms.region
                    : !ms.region && !currentLocationsDictionary[ms.sapId].region)
            )
        ) {
            updateLocations.push({
                id: currentLocationsDictionary[ms.sapId].id,
                name: ms.name,
                region: ms.region,
                active: currentLocationsDictionary[ms.sapId].active,
                percDiscountOverprice:
                currentLocationsDictionary[ms.sapId].percDiscountOverprice,
            });
        }
        // location uguale
        else if (ms.sapId) {
            sameLocations.push(currentLocationsDictionary[ms.sapId]);
        }
    });

    let insertedLocations = [];
    let updatedLocations = [];

    if (insertLocations.length > 0) {
        insertedLocations = await postLocationAll(insertLocations);
    }
    if (updateLocations.length > 0) {
        updatedLocations = await putLocationAll(updateLocations);
    }

    return insertedLocations.concat(updatedLocations).concat(sameLocations);
};

export const setFormNumberValue = (
    value: string,
    setValue: Function,
    fieldName: string,
    regex: RegExp
) => {
    const trimmedValue = value.trim();

    if (!value) setValue(fieldName, null);
    else if (regex.test(trimmedValue)) setValue(fieldName, trimmedValue);
};

export const ancillaryExceptionSetFormValues = (
    setValue: Function,
    value: AncillarySelectFE
) => {
    setValue("note", value.note as string);
    setValue("reservationFrom", value.reservationFrom);
    setValue("reservationTo", value.reservationTo);
    setValue("pickupFrom", value.pickupFrom);
    setValue("pickupTo", value.pickupTo);
    setValue("tpUnit", value.tpUnit);
    setValue("currency", value.currency);
    setValue(
        "amountMin",
        i18nConvertNumberWithDecimals(Number(value.amountMin) / 100)
    );
    setValue(
        "amountMax",
        i18nConvertNumberWithDecimals(Number(value.amountMax) / 100)
    );
    setValue("chargingWay", value.chargingWay as string);
    setValue("chargeType", value.chargeType);
    setValue("maxUnits", value.maxUnits);
};

export const i18nConvertNumberWithDecimals = (
    value: string | number
): string => {
    return i18n.language.includes("it")
        ? value?.toString().replace(".", ",")
        : value?.toString().replace(",", ".");
};

export const heightDynamicMatrix = (
    matrixRows: string[] | object[],
    headerMatrixHeight: number
) => {
    const rowMatrixHeight = 42;
    const rowMatrixHeightTotal = rowMatrixHeight * 10;
    if (matrixRows.length <= 10) {
        return (
            matrixRows.length * rowMatrixHeight +
            headerMatrixHeight +
            "px"
        ).toString();
    } else {
        return (headerMatrixHeight + rowMatrixHeightTotal + "px").toString();
    }
};
